import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewWorkflowStatus } from '../types';

/** Convert Firestore Timestamp, ISO string, or plain string to 'DD Mon YYYY' */
function tsToStr(val: unknown): string | undefined {
  if (!val) return undefined;
  if (val instanceof Timestamp) {
    return val.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (typeof val === 'object' && 'seconds' in (val as object)) {
    return new Date((val as { seconds: number }).seconds * 1000)
      .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (typeof val === 'string') return val;
  return String(val);
}

// ── Types ──────────────────────────────────────────────────────────────────────

export interface EPRSetupCycle {
  id: string;
  name: string;
  status: string;
  period: string;
  year?: number;
  startDate?: string;
  endDate?: string;
}

export interface EPREmployee {
  id: string;
  name: string;
  email: string;
  role: string;
  unitId?: string;
  unitName?: string;
  isActive: boolean;
}

export interface EPRReviewRow {
  formId: string;
  revieweeId: string;
  revieweeName: string;
  revieweeEmail?: string;
  managerId?: string;
  managerName?: string;
  unitId?: string;
  unitName?: string;
  status: ReviewWorkflowStatus;
  selfReviewSubmitted: boolean;
  managerReviewSubmitted: boolean;
  score360?: number;
  computedScore?: number;
  calibratedScore?: number;
  finalScore?: number;
  cycleId: string;
}

export interface EPRCycleStats {
  total: number;
  completed: number;
  pendingSelf: number;
  pendingManager: number;
  inCalibration: number;
  avgScore: number | null;
  completionPct: number;
}

// ── Cycle ──────────────────────────────────────────────────────────────────────

export const fetchAllCycles = async (): Promise<EPRSetupCycle[]> => {
  const snap = await getDocs(
    query(collection(db, 'reviewCycles'), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      name: String(data.name ?? ''),
      status: String(data.status ?? ''),
      period: `${data.period ?? ''} ${data.year ?? ''}`.trim(),
      year: data.year,
      startDate: tsToStr(data.startDate),
      endDate: tsToStr(data.endDate),
    };
  });
};

// ── Employees ──────────────────────────────────────────────────────────────────

export const fetchAllEmployees = async (): Promise<EPREmployee[]> => {
  const snap = await getDocs(
    query(collection(db, 'users'), where('isActive', '!=', false))
  );

  const unitsSnap = await getDocs(collection(db, 'units'));
  const unitsMap: Record<string, string> = {};
  unitsSnap.docs.forEach(d => {
    unitsMap[d.id] = String(d.data().name ?? d.id);
  });

  return snap.docs
    .filter(d => d.data().isDeleted !== true)
    .map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: String(data.name ?? ''),
        email: String(data.email ?? ''),
        role: String(data.role ?? 'viewer'),
        unitId: data.unitId,
        unitName: data.unitId ? (unitsMap[data.unitId] ?? data.unitId) : undefined,
        isActive: data.isActive !== false,
      };
    });
};

// ── Review Rows ────────────────────────────────────────────────────────────────

export const fetchCycleReviewRows = async (cycleId: string): Promise<EPRReviewRow[]> => {
  const formsSnap = await getDocs(
    query(collection(db, 'reviewForms'), where('cycleId', '==', cycleId))
  );
  if (formsSnap.empty) return [];

  const userIds = new Set<string>();
  formsSnap.docs.forEach(d => {
    const data = d.data();
    if (data.revieweeId) userIds.add(data.revieweeId);
    if (data.managerId) userIds.add(data.managerId);
  });

  const userMap: Record<string, { name: string; email: string; unitId?: string }> = {};
  await Promise.all(
    Array.from(userIds).map(async uid => {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        userMap[uid] = {
          name: String(data.name ?? ''),
          email: String(data.email ?? ''),
          unitId: data.unitId,
        };
      }
    })
  );

  const unitIds = new Set<string>();
  Object.values(userMap).forEach(u => { if (u.unitId) unitIds.add(u.unitId); });
  const unitMap: Record<string, string> = {};
  await Promise.all(
    Array.from(unitIds).map(async uid => {
      const unitDoc = await getDoc(doc(db, 'units', uid));
      if (unitDoc.exists()) unitMap[uid] = String(unitDoc.data().name ?? uid);
    })
  );

  return formsSnap.docs.map(d => {
    const data = d.data();
    const reviewee = userMap[data.revieweeId] ?? { name: data.revieweeName ?? '', email: '' };
    const manager = data.managerId ? userMap[data.managerId] : undefined;

    return {
      formId: d.id,
      revieweeId: data.revieweeId ?? '',
      revieweeName: reviewee.name || String(data.revieweeName ?? ''),
      revieweeEmail: reviewee.email,
      managerId: data.managerId,
      managerName: manager?.name,
      unitId: reviewee.unitId,
      unitName: reviewee.unitId ? unitMap[reviewee.unitId] : undefined,
      status: (data.status ?? 'prep_window') as ReviewWorkflowStatus,
      selfReviewSubmitted: ['self_submitted', '360_pending', '360_collected', 'manager_pending',
        'manager_submitted', 'calibration', 'approved', 'signed_off'].includes(data.status),
      managerReviewSubmitted: ['manager_submitted', 'calibration', 'approved', 'signed_off'].includes(data.status),
      score360: data.score360,
      computedScore: data.computedScore,
      calibratedScore: data.calibratedScore,
      finalScore: data.finalScore,
      cycleId: data.cycleId,
    };
  });
};

// ── Stats ──────────────────────────────────────────────────────────────────────

export const computeCycleStats = (rows: EPRReviewRow[]): EPRCycleStats => {
  const total = rows.length;
  const completed = rows.filter(r => ['approved', 'signed_off'].includes(r.status)).length;
  const pendingSelf = rows.filter(r => ['prep_window', 'self_pending'].includes(r.status)).length;
  const pendingManager = rows.filter(r =>
    ['self_submitted', '360_pending', '360_collected', 'manager_pending'].includes(r.status)
  ).length;
  const inCalibration = rows.filter(r =>
    ['calibration', 'manager_submitted'].includes(r.status)
  ).length;

  const scored = rows.filter(r => r.finalScore != null);
  const avgScore =
    scored.length > 0
      ? scored.reduce((sum, r) => sum + (r.finalScore as number), 0) / scored.length
      : null;

  return {
    total,
    completed,
    pendingSelf,
    pendingManager,
    inCalibration,
    avgScore,
    completionPct: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

// ── Mutations ──────────────────────────────────────────────────────────────────

export const assignManagerToForm = async (formId: string, managerId: string): Promise<void> => {
  await updateDoc(doc(db, 'reviewForms', formId), {
    managerId,
    updatedAt: serverTimestamp(),
  });
};

export const createReviewForm = async (
  cycleId: string,
  revieweeId: string,
  revieweeName: string,
  managerId?: string
): Promise<string> => {
  const formRef = doc(collection(db, 'reviewForms'));
  await setDoc(formRef, {
    cycleId,
    revieweeId,
    revieweeName,
    managerId: managerId ?? null,
    status: 'prep_window',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return formRef.id;
};
